import { ChevronLeft, ChevronRight } from "lucide-react";
import { Btn } from "@/components/admin/AdminShell";

const PAGE_SIZE = 10;

/**
 * Same pagination UI as Blog management:
 * "Showing X–Y of Z"  ·  Prev  ·  Page N of M  ·  Next
 *
 * sticky: pin to bottom of a flex column panel (Users/Blog style).
 * flush: bleed full width when parent has padding (Spot cards with -mx tables).
 */
export default function AdminPager({
  page = 1,
  total = 0,
  pageSize = PAGE_SIZE,
  loading = false,
  onPage,
  testId = "admin-pager",
  emptyLabel = "No rows",
  sticky = false,
  flush = false,
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);

  return (
    <div
      className={[
        "flex flex-wrap items-center justify-between gap-3 text-[12px] text-[#8892A8]",
        sticky ? "mt-auto shrink-0 border-t border-[#1E2845] px-4 py-3" : "mt-4 px-1",
        flush ? "-mx-5 px-5 pt-3 border-t border-[#1E2845]" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid={testId}
    >
      <span>
        {loading
          ? "Loading…"
          : total === 0
            ? emptyLabel
            : `Showing ${from}–${to} of ${total}`}
      </span>
      <div className="flex items-center gap-2 shrink-0">
        <Btn
          type="button"
          kind="ghost"
          className="h-[34px] px-3"
          disabled={loading || safePage <= 1}
          onClick={() => onPage?.(safePage - 1)}
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft size={14} /> Prev
        </Btn>
        <span className="tabular-nums min-w-[88px] text-center" data-testid={`${testId}-label`}>
          Page {safePage} of {totalPages}
        </span>
        <Btn
          type="button"
          kind="ghost"
          className="h-[34px] px-3"
          disabled={loading || safePage >= totalPages}
          onClick={() => onPage?.(safePage + 1)}
          data-testid={`${testId}-next`}
        >
          Next <ChevronRight size={14} />
        </Btn>
      </div>
    </div>
  );
}

export { PAGE_SIZE };
